/**
 * The money flow, as plain numbers — no drawing (STORY-185, FL-D1–FL-D5).
 *
 * **It works nothing out that the API did not send**: it only arranges the
 * amounts already there — money in by location, the four overhead groups and
 * profit — into blocks and ribbons whose sizes are proportional. Keeping it free
 * of any drawing is what lets the balance be tested.
 *
 * ── The balance ─────────────────────────────────────────────────────────────
 *
 * Money in plus any shortfall is exactly what leaves: costs plus profit. A loss
 * has no profit block, so a **Shortfall** block enters on the left, sized by the
 * gap (FL-D3), and the flow still adds up instead of hiding the loss.
 */

/** What a block is, so the drawing can colour and label it without guessing. */
export type FlowKind =
  "location" | "shortfall" | "middle" | "staff" | "suppliesLab" | "occupancy" | "other" | "profit";

export interface FlowBlock {
  id: string;
  label: string;
  kind: FlowKind;
  /** Minor units. Always above zero: a block with nothing in it is left out. */
  cents: number;
}

/** The blocks of a period: where it came in, and where it went. */
export interface Flow {
  sources: FlowBlock[];
  sinks: FlowBlock[];
  /** Everything that enters, and everything that leaves — equal by construction. */
  totalCents: number;
}

/** What `buildFlow` needs: the totals the API sent, summed over the period. */
export interface FlowInput {
  /** Money in by location id, for the locations selected. */
  byLocation: Readonly<Record<string, number>>;
  /** The selected locations, in the order the screen lists them, with their names. */
  locations: readonly { id: string; name: string }[];
  moneyIn: number;
  overhead: { staff: number; suppliesLab: number; occupancy: number; other: number };
  profit: number;
}

const SINKS = [
  ["staff", "Staff"],
  ["suppliesLab", "Supplies and lab"],
  ["occupancy", "Occupancy"],
  ["other", "Other"],
] as const;

/**
 * Arrange a period's money into a flow. Blocks worth nothing are dropped; a loss
 * adds a Shortfall source and drops profit (FL-D3).
 *
 * Money in by location may not add up to `moneyIn` if a payment's location is
 * outside the selection — it cannot be, the API filters by the same selection —
 * but if it ever did the difference would show as an **Unplaced** source, never
 * silently vanish, so the two sides could not disagree.
 */
export function buildFlow(input: FlowInput): Flow {
  const sources: FlowBlock[] = [];
  for (const location of input.locations) {
    const cents = input.byLocation[location.id] ?? 0;
    if (cents > 0) {
      sources.push({ id: `loc-${location.id}`, label: location.name, kind: "location", cents });
    }
  }
  const placed = sources.reduce((sum, block) => sum + block.cents, 0);
  if (input.moneyIn > placed) {
    sources.push({
      id: "unplaced",
      label: "Other locations",
      kind: "location",
      cents: input.moneyIn - placed,
    });
  }

  const sinks: FlowBlock[] = [];
  for (const [key, label] of SINKS) {
    const cents = input.overhead[key];
    if (cents > 0) sinks.push({ id: key, label, kind: key, cents });
  }
  const out = sinks.reduce((sum, block) => sum + block.cents, 0);

  const moneyIn = Math.max(input.moneyIn, placed);
  if (moneyIn > out) {
    sinks.push({ id: "profit", label: "Profit", kind: "profit", cents: moneyIn - out });
  } else if (out > moneyIn) {
    sources.push({ id: "shortfall", label: "Shortfall", kind: "shortfall", cents: out - moneyIn });
  }

  const totalCents = sources.reduce((sum, block) => sum + block.cents, 0);
  return { sources, sinks, totalCents };
}

/** A block placed on the page. */
export interface PlacedBlock extends FlowBlock {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** A ribbon from the right edge of one block to the left edge of another. */
export interface Ribbon {
  id: string;
  from: string;
  to: string;
  kind: FlowKind;
  cents: number;
  /** Top and bottom of the ribbon where it leaves, and where it arrives. */
  y0: number;
  y1: number;
  y2: number;
  y3: number;
  x0: number;
  x1: number;
}

export interface FlowLayout {
  sources: PlacedBlock[];
  middle: PlacedBlock;
  sinks: PlacedBlock[];
  ribbons: Ribbon[];
}

/** A block under this many pixels is drawn at this height and keeps its true figure (FL-D4). */
export const MIN_BLOCK_HEIGHT = 3;

/**
 * Place the blocks: three columns of equal total height (every column is
 * `totalCents` tall, so a ribbon never gets thicker or thinner on the way across),
 * with a gap between blocks in a column.
 *
 * Ribbons stack down the middle block in the order of the blocks they join, so
 * they never cross each other.
 */
export function layoutFlow(
  flow: Flow,
  box: { width: number; height: number; nodeWidth: number; gap: number },
): FlowLayout {
  const { width, height, nodeWidth, gap } = box;
  const widest = Math.max(flow.sources.length, flow.sinks.length, 1);
  const scale = flow.totalCents > 0 ? (height - gap * (widest - 1)) / flow.totalCents : 0;
  const heightOf = (cents: number): number => Math.max(MIN_BLOCK_HEIGHT, cents * scale);

  const column = (blocks: readonly FlowBlock[], x: number): PlacedBlock[] => {
    const used =
      blocks.reduce((sum, block) => sum + heightOf(block.cents), 0) +
      gap * Math.max(0, blocks.length - 1);
    let y = Math.max(0, (height - used) / 2);
    return blocks.map((block) => {
      const placed = { ...block, x, y, width: nodeWidth, height: heightOf(block.cents) };
      y += placed.height + gap;
      return placed;
    });
  };

  const sources = column(flow.sources, 0);
  const sinks = column(flow.sinks, width - nodeWidth);
  const middleHeight = Math.max(MIN_BLOCK_HEIGHT, flow.totalCents * scale);
  const middle: PlacedBlock = {
    id: "middle",
    label: "Money in",
    kind: "middle",
    cents: flow.totalCents,
    x: (width - nodeWidth) / 2,
    y: Math.max(0, (height - middleHeight) / 2),
    width: nodeWidth,
    height: middleHeight,
  };

  const ribbons: Ribbon[] = [];
  let down = middle.y;
  for (const source of sources) {
    const thick = source.cents * scale;
    ribbons.push({
      id: `in-${source.id}`,
      from: source.id,
      to: middle.id,
      kind: source.kind,
      cents: source.cents,
      x0: source.x + nodeWidth,
      x1: middle.x,
      y0: source.y,
      y1: source.y + source.height,
      y2: down,
      y3: down + thick,
    });
    down += thick;
  }
  down = middle.y;
  for (const sink of sinks) {
    const thick = sink.cents * scale;
    ribbons.push({
      id: `out-${sink.id}`,
      from: middle.id,
      to: sink.id,
      kind: sink.kind,
      cents: sink.cents,
      x0: middle.x + nodeWidth,
      x1: sink.x,
      y0: down,
      y1: down + thick,
      y2: sink.y,
      y3: sink.y + sink.height,
    });
    down += thick;
  }
  return { sources, middle, sinks, ribbons };
}
